const nonPersistableUniversalQueryKeys = [
  'limit',
  'offset',
  'orderBy',
  'page',
  'searchString',
  'sortOrder',
] as const;

export type NonPersistableUniversalQueryKey =
  (typeof nonPersistableUniversalQueryKeys)[number];

/**
 * Saved filters may reuse table query parameter names, but must not own
 * request state such as pagination, sorting, or the search query parameter.
 */
export function getPersistableUniversalQueryKeySet(
  queryKeys: readonly string[],
  pageOwnedKeys: readonly string[] = [],
) {
  const excludedKeys = new Set<string>([
    ...nonPersistableUniversalQueryKeys,
    ...pageOwnedKeys,
  ]);

  return new Set(queryKeys.filter((key) => !excludedKeys.has(key)));
}

/**
 * Creates a typed guard for page mappers that accept saved-filter clauses only
 * when the clause field is a persistable query key for that page.
 */
export function createPersistableQueryFilterKeyGuard<
  TQueryParams extends Record<string, unknown>,
  TPageOwnedKey extends Extract<keyof TQueryParams, string> = never,
>({
  pageOwnedKeys = [],
  queryKeys,
}: {
  readonly pageOwnedKeys?: readonly TPageOwnedKey[];
  readonly queryKeys: readonly Extract<keyof TQueryParams, string>[];
}) {
  const persistableQueryKeySet = getPersistableUniversalQueryKeySet(
    queryKeys,
    pageOwnedKeys,
  );

  return function isPersistableQueryFilterKey(
    field: string,
  ): field is Exclude<
    Extract<keyof TQueryParams, string>,
    NonPersistableUniversalQueryKey | TPageOwnedKey
  > {
    return persistableQueryKeySet.has(field);
  };
}
